import React from 'react';
import ItemViewer from './ItemViewer';
import { display } from 'components/ErrorView/ErrorDialog';
import {
  RemoveIcon,
  LoadingWrapper,
  ItemPickerList,
  RelatedItemChip,
  ItemPickerHeader,
  RelatedItemTitle,
  ItemPickerFooter,
  ItemPickerWrapper,
  ItemPickerBackButton,
  ItemPickerHeaderTitle,
  ItemPickerFooterTitle,
  ItemPickerListContainer,
  ItemPickerSaveButton,
} from './styles';
import BubbleLoading from 'components/BubbleLoading';
import Icon from 'components/Icon';
import Input from '../Input';
import HoverIcon from '../HoverIcon';
import { AxiosPromise } from 'axios';
import {
  IProviderItems,
  IProviderItemView,
  IRelatedProviderItem,
  ProviderItemType,
} from 'store/providers/providerTypes';
import { IItemPicker } from 'store/entity/types';
import i18n, { languageDirection } from '../../i18n';

interface IComponentProps {
  onRequireDocumentsInFolder: (dadFolderId: number) => Promise<any>;
  onSearchForDocuments?: (
    text: string,
    dadFolderId?: number
  ) => AxiosPromise<IProviderItemView[]>;
  onRequireFolderAndItems: (requiredFolderId: number) => any;
  onSaveItem: (item: IRelatedProviderItem) => void;
  providerId: number;
  providerItems: IProviderItems['items'];
  rootFolder: IItemPicker;
  saveTitle?: string;
  selectedProviderItem: IItemPicker | null;
}

interface IComponentStates {
  activeItem: IItemPicker | null;
  loading?: boolean;
  dadFolder: IItemPicker;
  searchFieldText?: string;
  searchQuery?: string;
  searchResult?: IItemPicker[];
}

class ItemPicker extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      activeItem: props.selectedProviderItem,
      loading: false,
      dadFolder: props.selectedProviderItem
        ? this.getDadFolderItem(props.selectedProviderItem.dadId)
        : props.rootFolder,
    };
  }

  doubleClick = false;

  handleSelectAItem =
    (selectedItem: IItemPicker) =>
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      if ((e as any).detail > 1) {
        // double click catch here and prevent the clicked item be selected
        return;
      }
      setTimeout(this.trigger(selectedItem), 200);
    };

  handleChangeActiveItem = (activeItem: IItemPicker | null) => {
    this.setState({ activeItem });
  };

  trigger = (selectedItem: IItemPicker) => () => {
    if (this.doubleClick === true) {
      this.doubleClick = false;
      return;
    }
    const { activeItem } = this.state;
    let item: IItemPicker | null;
    // unselect a selected item
    if (activeItem && selectedItem.id === activeItem.id) {
      item = null;
    } else {
      item = selectedItem;
    }
    this.handleChangeActiveItem(item);
  };

  handleDeleteRelatedItem = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    this.handleChangeActiveItem(null);
  };

  handleSaveItem = (directItem?: IItemPicker | null) => {
    const { activeItem } = this.state;
    const finalItem = directItem || activeItem;
    this.setState({ activeItem: finalItem });
    if (finalItem) {
      const { providerId, onSaveItem } = this.props;
      const item: IRelatedProviderItem = {
        providerId,
        title: finalItem.name,
        id: finalItem.id,
        type: finalItem.type,
      };
      onSaveItem(item);
    }
  };

  loadMoreItems = (dadFolder: IItemPicker) => {
    this.setState({ loading: true });
    this.props
      .onRequireDocumentsInFolder(dadFolder.id)
      .then(() => {
        this.setState({
          loading: false,
          dadFolder,
          searchQuery: undefined,
          searchResult: undefined,
        });
      })
      .catch((err: any) => {
        display(err);
        this.setState({ loading: false });
      });
  };

  /* read dadFolder's data from redux */
  getDadFolderItem = (dadFolderId: number) => {
    const dadFolderItem: IProviderItemView =
      this.props.providerItems[dadFolderId].data;
    return {
      id: dadFolderId,
      name: dadFolderItem
        ? dadFolderItem.name!
        : i18n.t('notLoaded', { ns: 'survey' }),
      dadId: dadFolderItem ? dadFolderItem.dadId : -1,
      type: 'folder' as ProviderItemType,
    };
  };

  /* load items in folder on click arrow icon */
  handleLoadMoreItems =
    (item: IItemPicker) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      this.doubleClick = true;
      this.loadMoreItems(item);
    };

  handleBackButton =
    (dadFolderId: number) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      const { rootFolder, onRequireFolderAndItems } = this.props;
      /* check dadFolder is rootFolder or not */
      if (this.state.searchResult !== undefined) {
        this.setState({ searchResult: undefined, searchQuery: undefined });
        return;
      }
      if (dadFolderId === rootFolder.id) {
        this.setState({
          dadFolder: rootFolder,
        });
      } else {
        this.setState({ loading: true });
        onRequireFolderAndItems(dadFolderId).then(() => {
          this.setState({
            loading: false,
            dadFolder: this.getDadFolderItem(dadFolderId),
          });
        });
      }
    };

  handleSearchForItems(query?: string) {
    if (!query || query.length === 0) {
      this.setState({ searchResult: undefined });
      return;
    }
    this.setState({ loading: true });
    this.props
      .onSearchForDocuments?.(query, this.state.dadFolder?.id)
      .then((result) => {
        this.setState({
          searchResult: result.data.map((itemData: IProviderItemView) => {
            return {
              id: itemData.id,
              name: itemData.name!,
              type: itemData.type,
              dadId: itemData.dadId,
              order: itemData.order,
            };
          }),
          loading: false,
          searchQuery: query,
        });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }
  render() {
    const { loading, dadFolder, activeItem, searchQuery } = this.state;
    const { rootFolder, providerItems, saveTitle } = this.props;
    // root folder's id is -1
    const showBackButton = dadFolder.dadId > 0;
    const filteredProviderItems = Object.entries(providerItems)
      .filter((item) => {
        return dadFolder
          ? item[1].data.dadId === dadFolder.id
          : item[1].data.dadId === rootFolder.id;
      })
      .reduce(
        (box, it) => {
          box[it[0]] = it[1];
          return box;
        },
        {} as IProviderItems['items']
      );

    const headerName = searchQuery
      ? i18n.t('searchFor', { ns: 'survey', searchQuery })
      : dadFolder.name;

    return (
      <ItemPickerWrapper>
        <ItemPickerListContainer>
          <ItemPickerHeader showBackButton={showBackButton}>
            {showBackButton && (
              <ItemPickerBackButton
                type={
                  languageDirection() === 'rtl' ? 'arrow-right' : 'arrow-left'
                }
                onClick={this.handleBackButton(dadFolder.dadId)}
                disabled={!!loading}
              />
            )}
            <ItemPickerHeaderTitle>{headerName}</ItemPickerHeaderTitle>
            <div style={{ flexGrow: 1 }} />
            {this.props.onSearchForDocuments && (
              <>
                <Input
                  id="search"
                  type="text"
                  style={{ height: '30px' }}
                  placeholder={i18n.t('search', { ns: 'survey' })}
                  value={this.state.searchFieldText}
                  onChange={(e) => {
                    this.setState({ searchFieldText: e.target.value });
                  }}
                  onKeyDown={(event) => {
                    if (event.currentTarget.value && event.keyCode === 13) {
                      this.handleSearchForItems(this.state.searchFieldText);
                    }
                  }}
                />
                <HoverIcon
                  type="search"
                  loading={loading}
                  color={'green'}
                  onClick={() =>
                    this.handleSearchForItems(this.state.searchFieldText)
                  }
                  aria-disabled={
                    this.state.searchFieldText?.replaceAll(' ', '') === ''
                  }
                />
              </>
            )}
          </ItemPickerHeader>
          {loading && (
            <LoadingWrapper>
              <BubbleLoading />
            </LoadingWrapper>
          )}
          <ItemPickerList loading={!!loading}>
            {(() => {
              const items =
                this.state.searchResult !== undefined
                  ? this.state.searchResult
                  : Object.values(filteredProviderItems || {})
                      .reduce((temp: any, item) => {
                        // to ignore items with creating status in Redux
                        if (item.data.status === 'deleted') {
                          return temp;
                        }
                        const itemStatus = item.status;
                        if (itemStatus && itemStatus === 'creating') {
                          return temp;
                        }
                        const itemData: IProviderItemView = item.data;
                        const itemType = itemData.type;
                        const isQuill =
                          itemData.document?.properties?.format === 'quill';
                        const entry = {
                          id: itemData.id,
                          name: itemData.name,
                          type: isQuill ? 'quill' : itemType,
                          dadId: itemData.dadId,
                          order: itemData.order,
                        };
                        temp.push(entry);
                        return temp;
                      }, [])
                      .sort((a: IItemPicker, b: IItemPicker) => {
                        if (!a.order) {
                          return 1;
                        }
                        if (!b.order) {
                          return -1;
                        }
                        return a.order - b.order;
                      });
              return items.length > 0 ? (
                items.map((file: IItemPicker) => (
                  <ItemViewer
                    key={file.id}
                    file={{
                      ...file,
                      name: file.name!,
                      type: file.type as ProviderItemType,
                    }}
                    onSelectAItem={this.handleSelectAItem}
                    isItemSelected={
                      activeItem ? activeItem.id === file.id : false
                    }
                    onLoadMoreItems={this.handleLoadMoreItems}
                    onDoubleClickNoneFolder={(item) => {
                      this.doubleClick = true;
                      this.handleSaveItem(item);
                    }}
                  />
                ))
              ) : (
                <p style={{ alignSelf: 'center', textAlign: 'center' }}>
                  {i18n.t('noItemsInFolder', { ns: 'survey' }) as string}
                </p>
              );
            })()}
          </ItemPickerList>
          <ItemPickerFooter>
            <ItemPickerFooterTitle>
              <span>
                {i18n.t('selectedContent', { ns: 'survey' }) as string}
              </span>
              <ItemPickerHeaderTitle className="myblue-text">
                {activeItem ? (
                  <RelatedItemChip>
                    <RelatedItemTitle>{activeItem.name}</RelatedItemTitle>
                    <RemoveIcon
                      role="button"
                      onClick={this.handleDeleteRelatedItem}
                      type="x"
                    />
                  </RelatedItemChip>
                ) : (
                  i18n.t('noItemSelected', { ns: 'survey' })
                )}
              </ItemPickerHeaderTitle>
            </ItemPickerFooterTitle>
            <ItemPickerSaveButton
              onClick={() => this.handleSaveItem()}
              isActive={!!activeItem}
            >
              <Icon
                type="check"
                className={languageDirection() === 'rtl' ? 'left' : 'right'}
              />
              {saveTitle ? saveTitle : i18n.t('save', { ns: 'survey' })}
            </ItemPickerSaveButton>
          </ItemPickerFooter>
        </ItemPickerListContainer>
      </ItemPickerWrapper>
    );
  }
}

export default ItemPicker;
